import { h } from "@stencil/core";
import { getAceBettingIconNames } from "./ace-betting-icon.get-names";
import { getIconUrl } from "./ace-betting-icon.icons";
/*
    START HERE

    How does this component work?

    1. Icons are stored in the assets folder using the ID.
    2. The icon may be different depending on the theme. In that case, the icon is stored in the corresponding theme folder. Only tipp3 has a different icon for the same sport. Icons that are the same for all themes are stored in the root assets folder and NOT duplicated in the theme folders.
    3. Using a command, those icons are converted to webp, then to b64 and stored in the iconMap object on the ace-betting-icon.icons.ts file.
    4. The icon name is mapped to an internal id in the iconIds object, which is the sport id coming from their system.
    5. So if you want to adjust or add a new icon, you need to:
        - Adjust/add the icon in the assets folder. If it's for a particular theme, put it in the corresponding folder. Make sure to use the same name as the ID and make sure that the file is compressed to the max.
        - Run the command to convert the icon to webp and b64: node scripts/generate-icons-map.cjs. This will update the ace-betting-icon.icons.ts file with the new icon URLs.
        - Add the new iconId in the iconIds object in this file (in case it has an internal id).
*/
export class AceBettingIcon {
    constructor() {
        this.size = '1rem';
        this.iconIds = {
            '1': 'soccer',
            '2': 'tennis',
            '3': 'ice-hockey',
            '4': 'basketball',
            '5': 'american-football',
            '6': 'baseball',
            '7': 'handball',
            '8': 'volleyball',
            '9': 'beach-volleyball',
            '10': 'formula-1',
            '11': 'rally',
            '12': 'motorbike',
            '13': 'alpine-skiing',
            '14': 'ski-jumping',
            '15': 'nordic-combined',
            '16': 'biathlon',
            '17': 'cross-country',
            '18': 'snowboard',
            '19': 'rugby',
            '20': 'athletic-sports',
            '21': 'cycling',
            '22': 'golf',
            '23': 'futsal',
            '24': 'beach-soccer',
            '25': 'darts',
            '26': 'snooker',
            '27': 'boxing',
            '28': 'bobsleigh',
            '29': 'luge',
            '30': 'skeleton',
            '31': 'speed-skating',
            '32': 'figure-skating',
            '33': 'table-tennis',
            '34': 'swimming',
            '35': 'waterpolo',
            '36': 'sailing',
            '37': 'rowing',
            '38': 'canoeing',
            '39': 'dancing-stars',
            '40': 'curling',
            '41': 'olympic-games',
            '42': 'badminton',
            '43': 'archery',
            '44': 'diving',
            '45': 'equestrian',
            '46': 'fencing',
            '47': 'gymnastics-artistic',
            '48': 'gymnastics-rhythmic',
            '49': 'modern-pentathlon',
            '50': 'shooting',
            '51': 'synchronised-swimming',
            '52': 'taekwondo',
            '53': 'triathlon',
            '54': 'weight-lifting',
            '55': 'wrestling',
            '56': 'judo',
            '57': 'hockey',
            '58': 'indoor-soccer',
            '59': 'darts-league',
            '60': 'short-track',
            '61': 'freestyle-ski',
            '62': 'nordic-ski-world-championships',
            '64': 'chess',
            '65': 'sport-mix',
            '66': 'boost',
            '67': 'chess-xxx',
            '68': 'e-sports-counter-strike',
            '69': 'e-sports-football',
            '70': 'e-sports-dota',
            '71': 'e-sports-fortnite',
            '72': 'e-sports-league-of-legends',
            '73': 'e-sports-star-craft',
            '76': 'e-sports-tennis',
            '77': 'basketball-3x3',
            '78': 'canoe-slalom',
            '79': 'open-water-swimming',
            '80': 'sport-climbing',
            '81': 'surfing',
            '82': 'breakdancing',
            '83': 'skateboarding',
            '999': 'specials',
            // Not from the file provided – they don't exist as icon IDs.
            //'-1': 'all-sports',
            '-2': 'favorites',
            '-3': 'hot',
            '-4': 'trophy',
            '-5': 'cricket',
            '-6': 'mma',
            '-7': 'nascar',
            '-8': 'padel',
            '-9': 'squash',
        };
    }
    componentWillLoad() {
        var _a;
        this.names = getAceBettingIconNames((_a = this.hostElement) === null || _a === void 0 ? void 0 : _a.id);
    }
    render() {
        var _a;
        let iconPath, themedIconPath;
        // If we have a sportId, get it directly.
        if (this.sportId) {
            iconPath = this.sportId;
        }
        // If we have a name, get it from the iconIds object.
        if (!this.sportId && this.name) {
            // Find the iconId that matches the name.
            const iconId = Object.keys(this.iconIds).find(key => this.iconIds[key] === this.name);
            if (iconId) {
                iconPath = iconId;
            }
        }
        if (!iconPath) {
            iconPath = '65'; // sport-mix
        }
        const computedStyle = getComputedStyle(this.hostElement);
        const currentTheme = computedStyle.getPropertyValue('--ace-theme-name');
        // If the current theme is tipp3, add the theme name to the icon path.
        const hasTipp3Theme = currentTheme && currentTheme === 'tipp3';
        if (hasTipp3Theme) {
            themedIconPath = `${iconPath}--${currentTheme}`;
        }
        else {
            themedIconPath = iconPath;
        }
        let iconUrl = getIconUrl(themedIconPath);
        if (!iconUrl && hasTipp3Theme) {
            iconUrl = getIconUrl(iconPath);
        }
        const inlineStyles = {
            '--ace-betting-icon-size': this.size,
        };
        const classes = {
            [this.names.CLASS.ROOT]: true,
        };
        return (h("img", { key: '1f813bf4fda37e8cc2c85fb6c3b3026d64030733', src: iconUrl, alt: (_a = this.alt) !== null && _a !== void 0 ? _a : '', style: inlineStyles, class: classes, id: this.names.ID.ROOT, loading: 'lazy' }));
    }
    static get is() { return "ace-betting-icon"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["./ace-betting-icon.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ace-betting-icon.css"]
        };
    }
    static get properties() {
        return {
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceBettingIconName",
                    "resolved": "\"alpine-skiing\" | \"american-football\" | \"archery\" | \"athletic-sports\" | \"badminton\" | \"baseball\" | \"basketball\" | \"basketball-3x3\" | \"beach-soccer\" | \"beach-volleyball\" | \"biathlon\" | \"bobsleigh\" | \"boost\" | \"boxing\" | \"breakdancing\" | \"canoeing\" | \"canoe-slalom\" | \"chess\" | \"chess-xxx\" | \"cricket\" | \"cross-country\" | \"curling\" | \"cycling\" | \"dancing-stars\" | \"darts\" | \"darts-league\" | \"diving\" | \"e-sports-counter-strike\" | \"e-sports-dota\" | \"e-sports-football\" | \"e-sports-fortnite\" | \"e-sports-league-of-legends\" | \"e-sports-star-craft\" | \"e-sports-tennis\" | \"equestrian\" | \"favorites\" | \"fencing\" | \"figure-skating\" | \"formula-1\" | \"freestyle-ski\" | \"futsal\" | \"golf\" | \"gymnastics-artistic\" | \"gymnastics-rhythmic\" | \"handball\" | \"hockey\" | \"hot\" | \"ice-hockey\" | \"indoor-soccer\" | \"judo\" | \"luge\" | \"mma\" | \"modern-pentathlon\" | \"motorbike\" | \"nascar\" | \"nordic-combined\" | \"nordic-ski-world-championships\" | \"olympic-games\" | \"open-water-swimming\" | \"padel\" | \"rally\" | \"rowing\" | \"rugby\" | \"sailing\" | \"shooting\" | \"short-track\" | \"skateboarding\" | \"skeleton\" | \"ski-jumping\" | \"snooker\" | \"snowboard\" | \"soccer\" | \"specials\" | \"speed-skating\" | \"sport-climbing\" | \"sport-mix\" | \"squash\" | \"surfing\" | \"swimming\" | \"synchronised-swimming\" | \"table-tennis\" | \"taekwondo\" | \"tennis\" | \"triathlon\" | \"trophy\" | \"volleyball\" | \"waterpolo\" | \"weight-lifting\" | \"wrestling\"",
                    "references": {
                        "IAceBettingIconName": {
                            "location": "import",
                            "path": "./ace-betting-icon.types",
                            "id": "src/components/ace-betting-icon/ace-betting-icon.types.ts::IAceBettingIconName"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "name",
                "reflect": false
            },
            "sportId": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "sport-id",
                "reflect": false
            },
            "size": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "size",
                "reflect": false,
                "defaultValue": "'1rem'"
            },
            "alt": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "alt",
                "reflect": false
            }
        };
    }
    static get elementRef() { return "hostElement"; }
}
//# sourceMappingURL=ace-betting-icon.js.map
